import { useState } from 'react';
import { Button } from "./Button";
import { DateTimeFormatter, Duration, LocalDateTime } from "@js-joda/core";
import { Card, Container } from "react-bootstrap";
import { facilityImg } from "../Data/Common";
import { useNavigate } from "react-router-dom";
import { ExtendSessionQuery, hourlyCheckoutAtom, vehicleFromExtendQuery } from "../Data/Atoms";
import { useAtom } from "jotai";
import { ReservationDetailsHourly } from "./ReservationDetailsHourly";
import { Facility } from "../Data/ApiTypes";
import { Locale } from "@js-joda/locale_en-us";

interface FacilityHourlyDetailsProps {
    facility: Facility;
    start:    LocalDateTime;
    end:      LocalDateTime;
    onStartChange?: (newStart: LocalDateTime) => void;
    onEndChange?: (newEnd: LocalDateTime) => void;
    onRatesClick?: () => void;
    extendMode?: boolean;
    extendSession?: ExtendSessionQuery;
}

export function FacilityHourlyDetails( { facility, start, end, onStartChange, onEndChange, onRatesClick, extendMode = false, extendSession } : FacilityHourlyDetailsProps ) {
    const nav = useNavigate();
    const [selOpt,         setSelOpt]         = useState<number>( 0 );
    const [hourlyCheckout, setHourlyCheckout] = useAtom( hourlyCheckoutAtom );
    const [rateAvailable, setRateAvailable] = useState(false);
    const [isLoadingRate, setIsLoadingRate] = useState(true);
    const [parkNow, setParkNow] = useState(true); // Default to Park Now
    const [parkNowMode, setParkNowMode] = useState<'duration' | 'until'>('duration');
    const duration = Duration.between( start, end );
    function continueToCheckoutClick() {
        let checkoutStart = start;
        let checkoutEnd = end;
        if (extendSession) {
            try {
                const formatter = DateTimeFormatter.ofPattern("M/d/uuuu h:mm a").withLocale(Locale.US);
                const parsedStart = LocalDateTime.parse(extendSession.startTime, formatter);
                const queryDurationHours = parseFloat(extendSession.duration ?? "");
                const extensionDuration = Number.isFinite(queryDurationHours) && queryDurationHours > 0
                    ? Duration.ofMinutes(queryDurationHours * 60)
                    : duration;
                checkoutStart = parsedStart;
                checkoutEnd = parsedStart.plus(extensionDuration);
            } catch {
                checkoutStart = start;
                checkoutEnd = end;
            }
        }
        const hasVehicleQuery = !!extendSession?.licensePlate && !!extendSession?.licenseState;
        const extendVehicle = hasVehicleQuery && extendSession
            ? [vehicleFromExtendQuery(extendSession.licensePlate, extendSession.licenseState)]
            : [];
        setHourlyCheckout( {
            facility: facility,
            start: checkoutStart,
            end: checkoutEnd,
            vehicles: extendVehicle,
            parkNow: parkNow,
            parkNowMode: parkNowMode,
            extendSession
        } );
        if (extendSession) {
            const params = new URLSearchParams();
            params.set("startTime", extendSession.startTime);
            if (extendSession.licensePlate) {
                params.set("licensePlate", extendSession.licensePlate);
            }
            if (extendSession.licenseState) {
                params.set("licenseState", extendSession.licenseState);
            }
            if (extendSession.duration) {
                params.set("duration", extendSession.duration);
            }
            nav(`/checkout/hourly?${params.toString()}`);
            return;
        }
        nav( "/checkout/hourly" );
    }

    function handleRateAvailable(isAvailable: boolean) {
        setRateAvailable(isAvailable);
    }

    function handleLoadingChange(loading: boolean) {
        setIsLoadingRate(loading);
    }

    function handleParkNowChange(isParkNow: boolean) {
        setParkNow(isParkNow);
    }

    function handleParkNowModeChange(mode: 'duration' | 'until') {
        setParkNowMode(mode);
    }
    return <div>
        <img className="rounded-1 d-none d-md-block"
            src={facilityImg( facility.images[0].storedImageId )}
            width="100%"
            style={{ aspectRatio: "20 / 11", objectFit: "cover" }} />
        <Container className="my-1">
            <div>
               
                <Card className="border-1 my-1 shadow border-2 border-primary">
                <Card.Header className="text-center border-0 header-gradient">                   
                        Parking Details
                    </Card.Header>
                    <Card.Body>
                        <ReservationDetailsHourly 
                            start={start} 
                            end={end} 
                            location={facility.name}  
                            facilityId={facility.facilityId} 
                            isCheckout={false}
                            onRateAvailable={handleRateAvailable}
                            onLoadingChange={handleLoadingChange}
                            onStartChange={onStartChange}
                            onEndChange={onEndChange}
                            onParkNowChange={handleParkNowChange}
                            onParkNowModeChange={handleParkNowModeChange}
                            extendMode={extendMode}
                            extendSession={extendSession}
                            editable={true}
                        />
                        {!isLoadingRate && !rateAvailable && (
                            <div  className="alert alert-warning mt-3" role="alert">
                                <strong>Rate Unavailable:</strong> Parking is not available during the selected time period. Please try a different time.
                                {onRatesClick && (
                                    <div className="mt-2">
                                        <button 
                                            type="button" 
                                            className="btn btn-link p-0 text-decoration-none text-primary"
                                            onClick={onRatesClick}
                                        >
                                            View Availability
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card.Body>
                </Card>
              
                <div className="d-flex justify-content-center">
                    <Button 
                        className="w-100 mb-2 mt-4 btn-main" 
                        onClick={continueToCheckoutClick}
                        disabled={isLoadingRate || !rateAvailable}
                    >
                        Book
                    </Button>
                </div>
                <hr className="border-primary border-2" />
                <div className="m-2" style={{ textAlign: "justify" }}>
                    {facility.entryInstructions}
                </div>
                <hr />
              
            </div>
        </Container>
    </div>;
};